import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useUserTier } from "@/hooks/useUserTier";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import AppFooter from "@/components/AppFooter";
import { useEffectiveBetaMode } from "@/hooks/useEffectiveBetaMode";
import { FlaskConical } from "lucide-react";

const SelectPlan = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const { tier: userTier } = useUserTier();
  const { effectiveBetaMode: BETA_MODE } = useEffectiveBetaMode();

  useEffect(() => {
    if (!authLoading && !user) navigate("/auth");
  }, [user, authLoading, navigate]);

  const { data: tiers, isLoading: tiersLoading } = useQuery({
    queryKey: ["subscription-tiers"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subscription_tiers")
        .select("*")
        .order("sort_order");
      if (error) throw error;
      return data;
    },
  });

  const handleSelect = async (tierId: string) => {
    if (!user) return;
    const { error } = await supabase
      .from("profiles")
      .update({ subscription_tier_id: tierId })
      .eq("user_id", user.id);
    if (error) {
      toast.error("Eroare la salvarea planului");
      return;
    }
    toast.success("Plan selectat cu succes!");
    navigate("/dashboard");
  };

  if (authLoading || tiersLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AppHeader />
      <div className="flex-1 flex flex-col items-center justify-center p-4">
      <div className="max-w-4xl w-full text-center mb-10">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-3">Alege-ți Planul</h1>
        <p className="text-muted-foreground text-lg">Selectează planul care ți se potrivește. Poți schimba oricând.</p>
        {BETA_MODE && (
          <div className="mt-6 mx-auto max-w-2xl rounded-lg border border-primary/30 bg-primary/5 p-4 flex items-start gap-3 text-left">
            <FlaskConical className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-foreground">Suntem în BETA PRIVAT</p>
              <p className="text-muted-foreground mt-1">
                Plățile sunt dezactivate temporar. Toți utilizatorii beneficiază gratuit de toate funcțiile pe planul activ. Planurile plătite vor fi disponibile după finalizarea procedurilor legale.
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl w-full">
        {tiers?.map((tier) => {
          const isFree = Number(tier.price_ron) === 0;
          const preselected = searchParams.get("tier") === tier.slug;
          const isActive = user && userTier?.id === tier.id;

          return (
            <div
              key={tier.id}
              className={`rounded-xl border p-6 flex flex-col relative ${
                isActive
                  ? "border-primary bg-primary/5 ring-2 ring-primary/50"
                  : preselected
                  ? "border-primary/50 bg-primary/5"
                  : "border-border bg-card"
              }`}
            >
              {isActive && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
                  Planul Tău Activ
                </span>
              )}
              <h3 className="font-heading text-xl font-bold">{tier.name}</h3>
              <div className="mt-4 mb-6">
                <span className="text-3xl font-extrabold font-heading">
                  {`${tier.price_ron} RON`}
                </span>
                {!isFree && (
                  <span className="text-muted-foreground text-sm">/lună</span>
                )}
              </div>
              <ul className="space-y-3 mb-8 flex-1">
                {(tier.features as string[])?.map((f: string, fi: number) => (
                  <li key={fi} className="flex items-start gap-2 text-sm">
                    <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                variant={isActive ? "secondary" : preselected ? "default" : "outline"}
                className="w-full"
                disabled={!!isActive || (BETA_MODE && !isFree)}
                onClick={() => handleSelect(tier.id)}
              >
                {isActive
                  ? "✓ Activ"
                  : BETA_MODE && !isFree
                  ? "Indisponibil în Beta"
                  : preselected
                  ? "Confirmă Planul"
                  : isFree
                  ? (user && userTier && Number(userTier.price_ron) !== 0 ? "Schimbă la Gratuit" : "Începe Gratuit")
                  : "Alege Planul"}
              </Button>
            </div>
          );
        })}
      </div>
      </div>
      <AppFooter />
    </div>
  );
};

export default SelectPlan;
